/**
 * MCPServerTemplates - Component for displaying available MCP server templates
 *
 * Features:
 * - Display list of pre-configured MCP server templates (Twilio, GitHub, Slack)
 * - Show required environment variables for each template
 * - One-click setup to pre-fill MCPServerConfig
 * - Show expected tools for each template
 * - Link to setup documentation
 *
 * Technical Design: /workspace/docs/technical/TDD-MCP-Integration.md Section 7
 * Ticket: AC-781
 */
import React, { useState, useEffect } from 'react';
import {
  Server,
  Phone,
  GitBranch,
  MessageSquare,
  ChevronRight,
  Loader,
  AlertCircle,
  Key,
  Wrench,
  ExternalLink,
} from 'lucide-react';
import apiService from '../../services/api';
import { MCPServerTemplate } from '../../types';

interface MCPServerTemplatesProps {
  onSelectTemplate: (template: MCPServerTemplate) => void;
}

/**
 * Get icon for template based on ID
 */
const getTemplateIcon = (templateId: string): React.ReactNode => {
  switch (templateId) {
    case 'twilio':
      return <Phone className="w-6 h-6" />;
    case 'github':
      return <GitBranch className="w-6 h-6" />;
    case 'slack':
      return <MessageSquare className="w-6 h-6" />;
    default:
      return <Server className="w-6 h-6" />;
  }
};

/**
 * Get background color class for template based on ID
 */
const getTemplateColorClass = (templateId: string): string => {
  switch (templateId) {
    case 'twilio':
      return 'bg-red-50 text-red-600 border-red-200';
    case 'github':
      return 'bg-gray-800 text-white border-gray-700';
    case 'slack':
      return 'bg-purple-50 text-purple-600 border-purple-200';
    default:
      return 'bg-primary-50 text-primary-600 border-blue-200';
  }
};

/**
 * Template card component
 */
const TemplateCard: React.FC<{
  template: MCPServerTemplate;
  onSelect: () => void;
}> = ({ template, onSelect }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div
      className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden hover:border-blue-300 hover:shadow-md transition-all"
      data-testid={`template-card-${template.id}`}
    >
      {/* Card Header */}
      <div className="p-4">
        <div className="flex items-start gap-4">
          {/* Icon */}
          <div
            className={`p-3 rounded-lg border ${getTemplateColorClass(template.id)}`}
            data-testid={`template-icon-${template.id}`}
          >
            {getTemplateIcon(template.id)}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <h4 className="text-base font-medium text-gray-900 dark:text-warm-white">{template.name}</h4>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{template.description}</p>

            {/* Quick info badges */}
            <div className="flex flex-wrap gap-2 mt-3">
              <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded">
                <Key className="w-3 h-3" />
                {template.required_env_vars.length} env var
                {template.required_env_vars.length !== 1 ? 's' : ''}
              </span>
              {template.expected_tools && template.expected_tools.length > 0 && (
                <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-primary-50 text-blue-700 rounded">
                  <Wrench className="w-3 h-3" />
                  {template.expected_tools.length} tool
                  {template.expected_tools.length !== 1 ? 's' : ''}
                </span>
              )}
            </div>
          </div>

          {/* Action button */}
          <button
            onClick={onSelect}
            className="flex-shrink-0 inline-flex items-center gap-1 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
            aria-label={`Use ${template.name}`}
          >
            Use Template
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Expandable details section */}
      <div className="border-t border-gray-100 dark:border-gray-700">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900 flex items-center justify-between"
          aria-expanded={isExpanded}
        >
          <span>View details</span>
          <ChevronRight
            className={`w-4 h-4 transform transition-transform ${isExpanded ? 'rotate-90' : ''}`}
          />
        </button>

        {isExpanded && (
          <div className="px-4 pb-4 space-y-4">
            {/* Required Environment Variables */}
            <div>
              <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
                <Key className="w-4 h-4" />
                Required Environment Variables
              </h5>
              <div className="space-y-2">
                {template.required_env_vars.map((envVar) => (
                  <div
                    key={envVar.name}
                    className="p-3 bg-gray-50 dark:bg-gray-900 rounded-md"
                    data-testid={`env-var-${envVar.name}`}
                  >
                    <div className="flex items-center gap-2">
                      <code className="text-sm font-mono text-gray-900 dark:text-warm-white">{envVar.name}</code>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{envVar.description}</p>
                    {envVar.help_text && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{envVar.help_text}</p>
                    )}
                    {envVar.example && (
                      <p className="text-xs text-gray-400 mt-1">
                        Example: <code className="font-mono">{envVar.example}</code>
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Expected Tools */}
            {template.expected_tools && template.expected_tools.length > 0 && (
              <div>
                <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
                  <Wrench className="w-4 h-4" />
                  Expected Tools
                </h5>
                <div className="flex flex-wrap gap-2">
                  {template.expected_tools.map((tool) => (
                    <span
                      key={tool}
                      className="px-2 py-1 text-xs font-mono bg-primary-50 text-blue-700 rounded"
                    >
                      {tool}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Documentation Link */}
            {template.setup_docs_url && (
              <div>
                <a
                  href={template.setup_docs_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 hover:underline"
                >
                  <ExternalLink className="w-4 h-4" />
                  View setup documentation
                </a>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const MCPServerTemplates: React.FC<MCPServerTemplatesProps> = ({ onSelectTemplate }) => {
  const [templates, setTemplates] = useState<MCPServerTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Load templates from API
   */
  useEffect(() => {
    const loadTemplates = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const data = await apiService.getMCPServerTemplates();
        setTemplates(data);
      } catch (err: any) {
        setError(err.message || 'Failed to load templates');
      } finally {
        setIsLoading(false);
      }
    };

    loadTemplates();
  }, []);

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader data-testid="loader-icon" className="w-6 h-6 animate-spin text-primary-600" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
        <div className="flex items-center gap-2 text-red-800">
          <AlertCircle className="w-5 h-5" />
          <span>Error loading templates: {error}</span>
        </div>
      </div>
    );
  }

  // Empty state (shouldn't normally happen as we have predefined templates)
  if (templates.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
        <Server className="w-12 h-12 mx-auto mb-4 text-gray-400" />
        <p>No templates available</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="mcp-server-templates">
      <div className="mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-warm-white">Quick Start Templates</h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          Select a pre-configured template to quickly set up an MCP server. You'll only need to
          provide your API credentials.
        </p>
      </div>

      <div className="grid gap-4">
        {templates.map((template) => (
          <TemplateCard
            key={template.id}
            template={template}
            onSelect={() => onSelectTemplate(template)}
          />
        ))}
      </div>
    </div>
  );
};

export default MCPServerTemplates;
